import { useCallback, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { GetDay, GetLog } from "../../App/Context"
import { useEditState } from "../../App/useEditState"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { EditGroup, EditGroupFieldDefinitions, saveErrorMessage } from "../../Components/EditGroup"
import { ApiClient, ILog, LogApi, ILogResponse } from "../../Types"

export const LogWatchViewData = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  saveLog(api: ApiClient, shipId: number, logId: number, values: any): Promise<ILogResponse> {
    return new LogApi(api).logs.update({ params: { shipId, logId }, body: values }).call();
  },
};

interface LogWatchViewProps {
  editable: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  onClick?: any;
  // Issue #1136: reports the save response's authoritative log back to `LogView`, so
  // its edit history resolves a just-saved field's "new value" from it rather than
  // from the (still pre-edit) `log` the day's `logs` list holds until the next live
  // broadcast / full day load. Optional: harnesses rendering this view standalone
  // have no edit history to keep in sync.
  recordSavedLog?: (log: ILog) => void;
  // Seeds the save-error state (issue #529) — for catalog/demo fixtures only;
  // production always starts with no error, only ever setting one via a real
  // rejected `onSave`.
  initSaveError?: unknown;
}

export function LogWatchView({ editable, initSaveError, onClick, recordSavedLog }: LogWatchViewProps) {
  const app = GetApp();
  const log = GetLog();
  const { recordAuditLogEdits, updateLog } = GetDay();
  const ft = app.getT("LogWatchView");
  const [edited, onEdited, resetEdited] = useEditState();
  const [saveError, setSaveError] = useState<unknown>(initSaveError);
  const editableFields: EditGroupFieldDefinitions[] = ["watch", "watchComment"];
  const formEditable = app.editable && editable;

  // `watch`/`watchComment` are both `.always` (see `Log/editability.ts`) and can never
  // actually be rejected by `logs.update`'s `EditNotAllowedError`, so there is no
  // field-specific rejection message here -- only the generic fallback, covering
  // network/other failures.
  const saveErrorText = useMemo(() => saveErrorMessage(saveError, {}, {
    fields: () => ft("save_error_generic"),
    generic: ft("save_error_generic"),
  }), [saveError, ft]);

  // Wired as EditGroup's `onSaveError` prop -- called with the rejected error on a
  // failed save, and with `undefined` at the start of every new submit attempt (see
  // EditGroup.tsx). A real error also resets `edited`, which is what stops
  // EditGroup's own auto-resubmit effect from silently resubmitting the now-reverted
  // (unchanged) form and clearing this error the instant that resubmit starts (issue
  // #1142). Mirrors useEditForm.ts's handleSaveError.
  const handleSaveError = useCallback((error: unknown) => {
    setSaveError(error);
    if (error !== undefined) {
      resetEdited();
    }
  }, [resetEdited]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const saveEdits = useCallback(async (_data: FormData, values: any) => {
    const result = await updateLog(log.id, values);
    if (result) {
      if (result.log) {
        recordSavedLog?.(result.log);
      }
      if (result.auditLogEdits) {
        recordAuditLogEdits(result.auditLogEdits);
      }
      resetEdited();
    }
  }, [log, recordAuditLogEdits, recordSavedLog, updateLog, resetEdited]);

  return (
    <Block className="LogView-watch block" onClick={onClick}>
      <EditGroup buttonFirst={true} changed={edited} editable={formEditable} editableFields={editableFields} error={saveErrorText} onSave={saveEdits} onSaveError={handleSaveError}>
        <BlockRow>
          <BlockField label={ft("watch")}>
            <input defaultValue={log.watch} name="watch" onChange={onEdited} readOnly={!formEditable} />
          </BlockField>
        </BlockRow>
        <BlockRow>
          <BlockField label={ft("watch_comment")}>
            <textarea defaultValue={log.watchComment} name="watchComment" onChange={onEdited} rows={4} readOnly={!formEditable}></textarea>
          </BlockField>
        </BlockRow>
      </EditGroup>
    </Block>
  );
}
